import type { NextSeoProps } from 'next-seo';
import { NextSeo } from 'next-seo';
import { useMemo } from 'react';
import { PRECONNECT_LINKS } from '~/constants/preconnectLinks';
import { useIsMobile } from '~/context/common/DeviceContext';
import { useDealershipMetaTag } from '~/context/dealership/StaticContext';
import {
  useGetSpinnerData,
  useGetVehicleDetails,
  useIsNewVehicle
} from '~/context/vdp/VehicleContext';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';

function useReplacementKeyValues() {
  const {
    year = '',
    make = '',
    model = '',
    mileage = '',
    trim = '',
    price = 0
  } = useGetVehicleDetails();

  return useMemo(
    () =>
      new Array<[string, string | number]>(
        ['{year}', year],
        ['{make}', make],
        ['{model}', model],
        ['{trim}', trim],
        ['{mileage}', mileage ? mileage.toLocaleString() : ''],
        ['{price}', price ? formatAndDropDecimal(price) : '']
      ),
    [make, mileage, model, price, trim, year]
  );
}

function useTitle() {
  const dealershipMetaTag = useDealershipMetaTag();
  const { year = '', make = '', model = '' } = useGetVehicleDetails();
  const replacementKeyValues = useReplacementKeyValues();

  return useMemo(
    () =>
      dealershipMetaTag
        ? replacementKeyValues.reduce(
            (acc, [key, value]) => acc.replace(key, value.toString()),
            dealershipMetaTag.title
          )
        : `${year} ${make} ${model} | Carvana`,
    [dealershipMetaTag, make, model, replacementKeyValues, year]
  );
}

function useDescription() {
  const dealershipMetaTag = useDealershipMetaTag();
  const {
    year = '',
    make = '',
    model = '',
    mileage = '',
    trim = '',
    price = 0
  } = useGetVehicleDetails();
  const isNewVehicle = useIsNewVehicle();
  const replacementKeyValues = useReplacementKeyValues();

  return useMemo(() => {
    if (dealershipMetaTag) {
      return replacementKeyValues.reduce(
        (acc, [key, value]) => acc.replace(key, value.toString()),
        dealershipMetaTag.metaDescription
      );
    }

    const vehicleType = isNewVehicle ? 'New' : 'Used';

    return price
      ? `${vehicleType} ${year} ${make} ${model} ${trim} for $${formatAndDropDecimal(
          price
        )} with ${mileage.toLocaleString()} miles. | Carvana`
      : `${vehicleType} ${year} ${make} ${model} ${trim} with ${mileage.toLocaleString()} miles. | Carvana`;
  }, [
    dealershipMetaTag,
    isNewVehicle,
    make,
    mileage,
    model,
    price,
    replacementKeyValues,
    trim,
    year
  ]);
}

export default function HeadTags(props: Partial<NextSeoProps>) {
  const { year, make, model, vehicleId, stockImageData } =
    useGetVehicleDetails();
  const { gif } = useGetSpinnerData();
  const isMobile = useIsMobile();
  const isNewVehicle = useIsNewVehicle();
  const title = useTitle();
  const description = useDescription();

  const jellyBeanPreview = isMobile
    ? stockImageData?.jellyBeanMobileUrl
    : stockImageData?.jellyBeanDesktopUrl;
  const previewImage = gif || jellyBeanPreview;
  const imageType = gif ? 'image/gif' : 'image/png';

  return (
    <NextSeo
      title={title}
      description={description}
      additionalLinkTags={PRECONNECT_LINKS}
      openGraph={{
        type: 'website',
        siteName: 'Carvana.com',
        title: `${year} ${make} ${model} - Carvana`,
        url: `${process.env.CVNA_APP_ABSOLUTE_BASE_URL}/vehicle/${vehicleId}`,
        description,
        images: previewImage
          ? [
              {
                url: `${previewImage}?width=600`,
                width: 600,
                height: 296,
                alt: `${
                  isNewVehicle ? '' : 'Used '
                }${year} ${make} ${model} | Carvana`
              }
            ]
          : undefined
      }}
      additionalMetaTags={[
        {
          property: 'og:image:type',
          content: `${imageType}`
        }
      ]}
      {...props}
    />
  );
}
